import { Directive, ElementRef, effect, inject, input } from "@angular/core";
import { RequestService } from "../services/request/request.service";

interface PresignedUrlResponse {
  status: boolean;
  result_code: number;
  message: string;
  data: {
    url: string;
  };
}

@Directive({
  selector: "[fhImageUrl]",
  standalone: true,
})
export class ImageUrlDirective {
  private requestService = inject(RequestService);

  originalUrl = input.required<string>();

  constructor(private el: ElementRef<HTMLImageElement>) {
    effect(() => {
      const imagePath = this.originalUrl();
      if (imagePath) {
        this.loadImage(imagePath);
      }
    });
  }

  private isValidImagePath(path: string): boolean {
    // Check if path has a valid file extension
    const validExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp", ".ico"];
    const lowerPath = path.toLowerCase();
    return validExtensions.some((ext) => lowerPath.endsWith(ext));
  }

  private async loadImage(path: string) {
    // Skip invalid paths (like "-", empty, or paths without extensions)
    if (!path || path === "-" || path.trim() === "" || !this.isValidImagePath(path)) {
      return;
    }

    // If already a full URL, use it directly
    if (path.startsWith("http")) {
      this.el.nativeElement.setAttribute("src", path);
      return;
    }

    try {
      // Get pre-signed URL from backend
      this.requestService
        // Silent: a page of images shouldn't flash the full-screen loader per image.
        .get<PresignedUrlResponse>(`/api/v1/image/presigned-url?path=${encodeURIComponent(path)}`, {
          silent: true,
        })
        .subscribe(async (response) => {
          if (response.status && response.data?.url) {
            // Set the image source directly
            // Modern browsers can handle SVG regardless of content-type header
            this.el.nativeElement.setAttribute("src", response.data.url);

            // Set Style
            this.el.nativeElement.style.objectFit = "contain";
            // set width & height if not already set
            if (!this.el.nativeElement.getAttribute("width")) {
              this.el.nativeElement.setAttribute("width", "100%");
            }
            if (!this.el.nativeElement.getAttribute("height")) {
              this.el.nativeElement.setAttribute("height", "100%");
            }
          } else {
            console.warn("Image not found or pre-signed URL unavailable:", path);
            // Optionally set a placeholder image or leave empty
          }
        });
    } catch (error) {
      console.warn("Error loading image:", path, error);
    }
  }
}
